import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import { formatNumber } from 'utils/currencyFormat';

import styles from './LineChart.module.scss';

const COLORS = {
  myBooking: '#165DFF',
  peerBooking: '#00BBFF',
  myPrice: '#FF7A00',
  peerPrice: '#2CC4A2',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className={styles.Tooltip}>
      <p className={styles.TooltipTitle}>{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={styles.TooltipRow}>
          <span
            className={styles.TooltipDot}
            style={{ backgroundColor: entry.color }}
          />
          <span className={styles.TooltipLabel}>
            {entry.name}
            :
          </span>
          <span className={styles.TooltipValue}>
            {`${formatNumber(entry.value)}%`}
          </span>
        </div>
      ))}
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string,
    }),
  ),
  label: PropTypes.string,
};

CustomTooltip.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

function LineChart({
  data,
  myBookingName,
  peerBookingName,
  myPriceName,
  peerPriceName,
}) {
  const maxBookingRatio = useMemo(() => {
    if (!data || !data.length) return 100;
    const maxValue = Math.max(
      ...data.map((item) => Math.max(
        Number(item?.myBooking) || 0,
        Number(item?.peerBooking) || 0,
        Number(item?.myPrice) || 0,
        Number(item?.peerPrice) || 0,
      )),
    );
    return Math.max(10, Math.ceil(maxValue / 5) * 5);
  }, [data]);

  return (
    <ResponsiveContainer width="100%" height={380}>
      <ComposedChart
        data={data}
        margin={{
          top: 10,
          right: 10,
          left: 0,
          bottom: 0,
        }}
        barCategoryGap="20%"
        barGap={2}
      >
        <CartesianGrid
          strokeDasharray="4 4"
          vertical={false}
          stroke="#E5E6EB"
        />

        <XAxis
          dataKey="name"
          tick={{ fontSize: 13, fill: '#86909C' }}
          axisLine={{ stroke: '#E5E6EB' }}
          tickLine={false}
        />

        <YAxis
          yAxisId="booking"
          tick={{ fontSize: 13, fill: '#86909C' }}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          domain={[0, maxBookingRatio]}
          tickFormatter={(v) => `${formatNumber(v)}%`}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: 'rgba(0,0,0,0.04)' }}
        />

        <Bar
          dataKey="myBooking"
          yAxisId="booking"
          name={myBookingName}
          fill={COLORS.myBooking}
          barSize={52}
        />

        <Bar
          dataKey="peerBooking"
          yAxisId="booking"
          name={peerBookingName}
          fill={COLORS.peerBooking}
          barSize={52}
        />

        <Line
          type="linear"
          dataKey="myPrice"
          yAxisId="booking"
          name={myPriceName}
          stroke={COLORS.myPrice}
          strokeWidth={2}
          dot={{ fill: COLORS.myPrice, stroke: '#FFF', strokeWidth: 1.5, r: 4 }}
          activeDot={{ r: 6 }}
        />

        <Line
          type="linear"
          dataKey="peerPrice"
          yAxisId="booking"
          name={peerPriceName}
          stroke={COLORS.peerPrice}
          strokeWidth={2}
          dot={{ fill: COLORS.peerPrice, stroke: '#FFF', strokeWidth: 1.5, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

LineChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      myBooking: PropTypes.number,
      peerBooking: PropTypes.number,
      myPrice: PropTypes.number,
      peerPrice: PropTypes.number,
    }),
  ).isRequired,
  myBookingName: PropTypes.string,
  peerBookingName: PropTypes.string,
  myPriceName: PropTypes.string,
  peerPriceName: PropTypes.string,
};

LineChart.defaultProps = {
  myBookingName: 'Đặt trước của tôi',
  peerBookingName: 'Đặt trước cùng phân khúc',
  myPriceName: 'Giá bình quân của tôi',
  peerPriceName: 'Giá bình quân cùng phân khúc',
};

export default React.memo(LineChart);
